// Webhooks — owner-only management of outbound payout-status webhooks.
//
// Security constraints (mirrors ApiKeys.tsx):
//   - The one-time `secret` is held in React state from the moment the
//     create resolves until the RevealSecret modal closes. Never written
//     to localStorage / sessionStorage / cookies.
//   - Owner+ gate matches the backend chain (RequireMembershipRole(owner)
//     on every CRUD route). Members below owner see an "owner-only"
//     empty state — the sidebar gate hides the page from them anyway.
//   - When the merchant's api_enabled flag is false, the backend 403s
//     with api_disabled — we surface that as a friendly notice.
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { apiErrorMessage } from "@/utils/apiError";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import {
  ALL_WEBHOOK_EVENTS,
  createWebhook,
  disableWebhook,
  listDeliveries,
  listWebhooks,
  testWebhook,
  type CreateWebhookResult,
  type WebhookEvent,
  type WebhookItem,
} from "@/api/webhooks";

// ─── Hooks ───────────────────────────────────────────────────────────────

function useWebhooks() {
  const merchantId = useUI((s) => s.currentMerchantId);
  return useQuery({
    queryKey: ["webhooks", merchantId],
    queryFn: () => listWebhooks(merchantId!),
    enabled: merchantId != null,
    staleTime: 10_000,
  });
}

// ─── Create modal ────────────────────────────────────────────────────────

function CreateWebhookModal({
  open,
  onClose,
  merchantId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  merchantId: number;
  onCreated: (r: CreateWebhookResult) => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<Record<WebhookEvent, boolean>>({
    "payout.approved": true,
    "payout.success": true,
    "payout.failure": true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedEvents = ALL_WEBHOOK_EVENTS.filter((e) => events[e]);
  const canSubmit =
    url.trim().startsWith("https://") &&
    selectedEvents.length > 0 &&
    !submitting;

  function resetAndClose() {
    setUrl("");
    setDescription("");
    setEvents({
      "payout.approved": true,
      "payout.success": true,
      "payout.failure": true,
    });
    setSubmitting(false);
    setError(null);
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createWebhook(merchantId, {
        url: url.trim(),
        events: selectedEvents,
        description: description.trim() || undefined,
      });
      void qc.invalidateQueries({ queryKey: ["webhooks", merchantId] });
      resetAndClose();
      onCreated(result);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create the webhook."));
    } finally {
      setSubmitting(false);
    }
  }

  function toggleEvent(ev: WebhookEvent) {
    setEvents((prev) => ({ ...prev, [ev]: !prev[ev] }));
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Register webhook"
      foot={
        <>
          <button className="btn ghost" type="button" onClick={resetAndClose} disabled={submitting}>
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="create-webhook-form"
            disabled={!canSubmit}
          >
            {submitting ? "Creating…" : "Create webhook"}
          </button>
        </>
      }
    >
      <form id="create-webhook-form" onSubmit={handleSubmit} className="stack-16">
        <div className="field">
          <label className="label" htmlFor="webhook-url">Endpoint URL</label>
          <input
            id="webhook-url"
            className="input"
            type="url"
            placeholder="https://your-server.example/webhooks/payouts"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
            required
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            HTTPS only. Each delivery POSTs JSON with an HMAC-SHA256
            signature in <code>X-Webhook-Signature</code>.
          </div>
        </div>

        <div className="field">
          <label className="label">Subscribed events</label>
          <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
            {ALL_WEBHOOK_EVENTS.map((ev) => (
              <label
                key={ev}
                className="row gap-6"
                style={{
                  padding: "5px 10px",
                  border: "1px solid " + (events[ev] ? "var(--ink-2)" : "var(--hair)"),
                  borderRadius: 3,
                  fontSize: 12,
                  background: events[ev] ? "var(--tint)" : "transparent",
                  cursor: "pointer",
                }}
              >
                <input type="checkbox" checked={events[ev]} onChange={() => toggleEvent(ev)} />
                <span className="mono" style={{ fontSize: 11 }}>{ev}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="webhook-desc">Description (optional)</label>
          <input
            id="webhook-desc"
            className="input"
            type="text"
            placeholder="e.g. Production · accounting service"
            maxLength={256}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoComplete="off"
          />
        </div>

        {error && <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>}
      </form>
    </Modal>
  );
}

// ─── Reveal-secret modal ─────────────────────────────────────────────────

function RevealSecretModal({
  result,
  onClose,
}: {
  result: CreateWebhookResult | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!result) return null;

  function copy() {
    navigator.clipboard?.writeText(result!.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Your signing secret"
      wide
      disableScrimClose
      hideCloseButton
      foot={
        <>
          <div className="flex-1" />
          <button className="btn primary" type="button" onClick={onClose}>
            I've saved this secret
          </button>
        </>
      }
    >
      <div className="stack-16">
        <div
          style={{
            background: "var(--warn-soft)",
            padding: 12,
            borderRadius: 3,
            borderLeft: "2px solid var(--warn)",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <I.AlertCircle size={14} style={{ marginTop: 2, color: "var(--warn)" }} />
          <div style={{ fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>
              This is the only time you'll see this secret.
            </span>
            <span className="dim">
              {" "}Store it in your secret manager — you'll use it to verify
              the HMAC signature on every delivery. If you lose it, disable
              this webhook and create a new one.
            </span>
          </div>
        </div>

        <div className="field">
          <label className="label">Signing secret (hex)</label>
          <div className="input-group">
            <input
              className="input mono"
              readOnly
              value={result.secret}
              style={{ letterSpacing: "0.03em" }}
            />
            <button
              className="addon"
              type="button"
              onClick={copy}
              style={{ cursor: "pointer", padding: "0 12px", color: "var(--ink)" }}
            >
              {copied ? (
                <><I.Check size={11} stroke={2.5} /> Copied</>
              ) : (
                <><I.Copy size={11} /> Copy</>
              )}
            </button>
          </div>
        </div>

        <dl className="dl tight">
          <dt>Endpoint</dt>
          <dd className="mono" style={{ fontSize: 12 }}>{result.webhook.url}</dd>
          <dt>Events</dt>
          <dd>
            <div className="row" style={{ flexWrap: "wrap", gap: 4 }}>
              {result.webhook.events.map((e) => (
                <span
                  key={e}
                  className="mono"
                  style={{
                    fontSize: 11,
                    padding: "1px 6px",
                    border: "1px solid var(--hair)",
                    borderRadius: 2,
                  }}
                >{e}</span>
              ))}
            </div>
          </dd>
          <dt>Fingerprint</dt>
          <dd className="mono">{result.webhook.secret_fingerprint}</dd>
        </dl>

        <div className="dim" style={{ fontSize: 11 }}>
          Verify signatures on incoming deliveries: HMAC-SHA256 of{" "}
          <code>{"<timestamp>.<raw_body>"}</code> using this secret,
          hex-encoded, must match the <code>v1=</code> segment of
          the <code>X-Webhook-Signature</code> header. See the API docs.
        </div>
      </div>
    </Modal>
  );
}

// ─── Deliveries modal ────────────────────────────────────────────────────

function DeliveriesModal({
  merchantId,
  webhook,
  onClose,
}: {
  merchantId: number;
  webhook: WebhookItem | null;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["webhook-deliveries", merchantId, webhook?.id],
    queryFn: () => listDeliveries(merchantId, webhook!.id),
    enabled: webhook != null,
    refetchInterval: 4_000,
  });
  if (!webhook) return null;
  const items = q.data ?? [];
  return (
    <Modal
      open={true}
      onClose={onClose}
      wide
      title={`Deliveries · ${webhook.url}`}
      foot={
        <>
          <div className="flex-1" />
          <button className="btn ghost" type="button" onClick={onClose}>Close</button>
        </>
      }
    >
      {q.isLoading ? (
        <div className="dim">Loading deliveries…</div>
      ) : items.length === 0 ? (
        <div className="dim">No deliveries yet. Send a test to verify the endpoint.</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Event</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>HTTP</th>
              <th>Created</th>
              <th>Delivered</th>
              <th>Last error</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td className="mono" style={{ fontSize: 11 }}>{d.event_type}</td>
                <td><StatusPill status={d.status} /></td>
                <td className="mono dim">{d.attempt_count}</td>
                <td className="mono dim">{d.last_status ?? "—"}</td>
                <td className="mono dim" style={{ fontSize: 11 }}>{formatDateTime(d.created_at)}</td>
                <td className="mono dim" style={{ fontSize: 11 }}>
                  {d.delivered_at ? formatDateTime(d.delivered_at) : d.failed_at ? "(failed)" : "—"}
                </td>
                <td className="dim" style={{ fontSize: 11, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {d.last_error || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function Webhooks() {
  const merchantId = useUI((s) => s.currentMerchantId);
  const { data: webhooks = [], isLoading } = useWebhooks();
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  const myMembership = useAuth((s) =>
    s.user?.memberships.find((m) => m.merchant_id === merchantId),
  );
  const merchantAPIEnabled = myMembership?.api_enabled ?? false;
  const myRole = myMembership?.role;
  // Operator+ matches the backend chain. Mirrors api-keys: anyone who
  // can mint a key can also register the webhook endpoint listening
  // on the other end of that key.
  const canManage =
    isAdmin || myRole === "owner" || myRole === "operator";
  const apiDisabledForMember = !isAdmin && !merchantAPIEnabled;

  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState<CreateWebhookResult | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookItem | null>(null);

  const qc = useQueryClient();
  const disableMut = useMutation({
    mutationFn: (id: number) => disableWebhook(merchantId!, id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["webhooks", merchantId] }),
  });
  const testMut = useMutation({
    mutationFn: (id: number) => testWebhook(merchantId!, id),
  });

  if (apiDisabledForMember) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Console · Developer</div>
            <h1>Webhooks</h1>
            <div className="sub">API access is currently disabled for this merchant.</div>
          </div>
        </div>
        <div className="card">
          <div className="empty">
            <div className="icon"><I.Bell size={16} /></div>
            <h3>API access is turned off</h3>
            <p>Your administrator has disabled programmatic API access for this merchant. Contact them to enable webhooks.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Console · Developer</div>
            <h1>Webhooks</h1>
            <div className="sub">Operator-only surface.</div>
          </div>
        </div>
        <div className="card">
          <div className="empty">
            <div className="icon"><I.Bell size={16} /></div>
            <h3>Operator permission required</h3>
            <p>Webhook configuration is restricted to merchant operators and owners. Ask one of them to manage these endpoints.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Developer</div>
          <h1>Webhooks</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : `${webhooks.length} endpoint${webhooks.length === 1 ? "" : "s"} · payout status events`}
          </div>
        </div>
        <div className="actions">
          <Link className="btn ghost" to="/keys/docs"><I.Info size={12} /> API reference</Link>
          {merchantId != null && (
            <button className="btn accent" type="button" onClick={() => setShowCreate(true)}>
              <I.Plus size={12} /> Register webhook
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          background: "var(--bg-sunk)",
          padding: "10px 14px",
          borderRadius: 3,
          borderLeft: "2px solid var(--accent)",
          marginBottom: 16,
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        <I.Info size={14} style={{ marginTop: 2, color: "var(--accent)" }} />
        <div style={{ fontSize: 12 }}>
          <span style={{ fontWeight: 500 }}>Signed deliveries.</span>
          <span className="dim">
            {" "}Every event is POSTed to your URL with an HMAC-SHA256
            signature in <code>X-Webhook-Signature</code>. Retries follow
            exponential backoff up to 5 attempts (~13 min total) before
            we dead-letter. Subscribe to whatever subset of events you need.
          </span>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="empty"><div className="dim">Loading endpoints…</div></div>
        ) : webhooks.length === 0 ? (
          <div className="empty">
            <div className="icon"><I.Bell size={16} /></div>
            <h3>No webhooks registered</h3>
            <p>Register an HTTPS endpoint to receive push notifications for payout status changes.</p>
            {merchantId != null && (
              <button className="btn primary" type="button" onClick={() => setShowCreate(true)}>
                <I.Plus size={12} /> Register your first webhook
              </button>
            )}
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>URL</th>
                <th>Events</th>
                <th>Status</th>
                <th>Last status</th>
                <th>Last success</th>
                <th>Last failure</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {webhooks.map((w) => {
                const isActive = w.status === "active";
                return (
                  <tr key={w.id}>
                    <td>
                      <div className="cell-name mono" style={{ fontSize: 12 }}>{w.url}</div>
                      {w.description && (
                        <div className="cell-id dim" style={{ fontSize: 11 }}>{w.description}</div>
                      )}
                      <div className="cell-id dim" style={{ fontSize: 10.5 }}>
                        fp:{w.secret_fingerprint}
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ flexWrap: "wrap", gap: 4 }}>
                        {(w.events.length === 0 ? ALL_WEBHOOK_EVENTS : w.events).map((e) => (
                          <span
                            key={e}
                            className="mono"
                            style={{
                              fontSize: 10.5,
                              padding: "1px 6px",
                              border: "1px solid var(--hair)",
                              borderRadius: 2,
                            }}
                          >{e}</span>
                        ))}
                      </div>
                    </td>
                    <td><StatusPill status={w.status} /></td>
                    <td className="mono dim">{w.last_status ?? "—"}</td>
                    <td className="mono dim" style={{ fontSize: 11 }}>
                      {w.last_success_at ? formatDateTime(w.last_success_at) : "—"}
                    </td>
                    <td className="mono dim" style={{ fontSize: 11 }}>
                      {w.last_failure_at ? formatDateTime(w.last_failure_at) : "—"}
                    </td>
                    <td className="right">
                      <div className="row gap-4" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() => setDeliveriesFor(w)}
                        >
                          Deliveries
                        </button>
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() => testMut.mutate(w.id)}
                          disabled={!isActive || testMut.isPending}
                          title={isActive ? "Send a synthetic test delivery" : "Endpoint is disabled"}
                        >
                          Test
                        </button>
                        <button
                          className="btn ghost sm danger"
                          type="button"
                          onClick={() => {
                            if (confirm("Disable this webhook? Deliveries stop immediately. This cannot be undone — create a new endpoint to re-enable.")) {
                              disableMut.mutate(w.id);
                            }
                          }}
                          disabled={!isActive || disableMut.isPending}
                          title={isActive ? "Disable webhook" : "Already disabled"}
                        >
                          Disable
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {merchantId != null && (
        <CreateWebhookModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          merchantId={merchantId}
          onCreated={(r) => setRevealed(r)}
        />
      )}
      <RevealSecretModal result={revealed} onClose={() => setRevealed(null)} />
      {merchantId != null && (
        <DeliveriesModal
          merchantId={merchantId}
          webhook={deliveriesFor}
          onClose={() => setDeliveriesFor(null)}
        />
      )}
    </div>
  );
}
